import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { PlIcon } from '@app/icon-library/icons';
import { IconColor } from '@shared/models/step.model';
import { ButtonPadding } from '@app/components/button/button.component';

import * as c3 from 'c3';
import { ChartAPI, PrimitiveArray } from 'c3';
import { WindowEventService } from '@shared/services/window.event.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs';
import { ClearTimeoutAbstractComponent } from '@shared/abstract/clearTimeout.abstract.component';

@Component({
  selector: 'pl-donut',
  templateUrl: './donut.component.html',
  styleUrls: ['./donut.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DonutComponent extends ClearTimeoutAbstractComponent implements AfterViewInit {
  @Input() public title: string = '';
  @Input() public subtitle: string = '';
  @Input() public isLocked: boolean = false;
  @Input({ required: true }) public percentage!: number;

  @ViewChild('chartContainer', { static: true }) public chartContainer!: ElementRef;

  public readonly IconColor: typeof IconColor = IconColor;
  public readonly PlIcon: typeof PlIcon = PlIcon;
  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;

  // Used to generate css class name, if you change this, you have to change css class name selectors in donut.component.scss
  private readonly DATA_COLUMN_DEF: string = 'pl-data';
  private readonly REMAINING_COLUMN_DEF: string = 'pl-remaining';

  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(private windowEventService: WindowEventService) {
    super();
  }

  public ngAfterViewInit(): void {
    const chart: ChartAPI = c3.generate({
      bindto: this.chartContainer.nativeElement,
      data: {
        colors: {
          [this.DATA_COLUMN_DEF]: this.isLocked ? '#b1b2b4' : '#6cad58',
          [this.REMAINING_COLUMN_DEF]: 'transparent',
        },
        columns: this.getColumnsDefinition(),
        type: 'donut',
        order: null,
      },
      donut: {
        title: `${this.percentage}%`,
        label: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        show: false,
      },
    });

    this.windowEventService
      .getWindowResizeEvent()
      .pipe(distinctUntilChanged())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        chart.flush();
      });

    // Sometimes, the donut do not render properly, they are too small,
    // forcing them to re-render solves this random and hard to reproduce bug
    this.addTimeOutIdToClearOnDestroy(
      setTimeout(() => {
        chart.flush();
      }, 100),
    );
  }

  private getColumnsDefinition(): [string, ...PrimitiveArray][] {
    const remaining: number = 100 - this.percentage;
    const dataDef: [string, ...number[]] = [this.DATA_COLUMN_DEF, this.percentage];
    const remainingDef: [string, ...number[]] = [this.REMAINING_COLUMN_DEF, remaining];

    return [dataDef, remainingDef];
  }
}
